// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { api } from '../api/client';
import {
  EventStream,
  activeJobs,
  bumpCloudRevisions,
  type CloudRevisions,
  emptyJobState,
  jobsNewestFirst,
  type ConnectionStatus,
  type JobState,
} from '../api/events';
import type { Job, ModelChangedEvent, PanoramasChangedEvent, PosegraphChangedEvent, SurveyChangedEvent } from '../api/types';
import { clearViewerCache, invalidateClouds, invalidateMeshes, noteFinishedJobs } from '../viewport/viewerCache';
import { finishedJobIds } from './jobSession';
import { ALL_CASES_PATH } from './navigation';

interface JobsContextValue {
  state: JobState;
  status: ConnectionStatus;
  /** Newest first. */
  jobs: Job[];
  /** Jobs that are `queued` or `running`. */
  active: Job[];
  /**
   * Per-cloud revisions, bumped by the `clouds.changed` event. The viewport
   * restarts a cloud's stream and reloads the cloud list when they move.
   */
  cloudRevisions: CloudRevisions;
  /**
   * A write this client made changed `names`. The server broadcasts
   * `clouds.changed` for it; this bumps locally only when the socket is not
   * open to deliver that, so an edit is never left stale on screen.
   */
  markCloudsChanged: (names: readonly string[]) => void;
  /**
   * The latest `survey.changed` (a survey, sample or resource write by any
   * client), a new object per event; null until one arrives. The case store
   * re-reads what it names.
   */
  surveyChange: SurveyChangedEvent | null;
  /**
   * The latest `panoramas.changed` (a panorama placed or added by any
   * client), a new object per event; null until one arrives. The Ground
   * re-reads its panoramas.
   */
  panoramasChange: PanoramasChangedEvent | null;
  /**
   * The latest `posegraph.changed` (a pose-graph editor write by any
   * client), a new object per event; null until one arrives. The Posegraf
   * editor refetches the graph, the blocks and the versions.
   */
  posegraphChange: PosegraphChangedEvent | null;
  /**
   * The latest `model.changed` (a solid-model edit, undo or redo by any
   * client, ruxd-5uv), or null before one arrived: Rediger › Geometri and
   * the plan re-read the walls, openings, spaces and storeys on it.
   */
  modelChange: ModelChangedEvent | null;
  /**
   * The jobs already finished at the first `hello` (`jobSession.ts`): history,
   * not news — no toast, no refresh. Null until the `hello` has arrived.
   */
  history: ReadonlySet<string> | null;
}

const JobsContext = createContext<JobsContextValue | null>(null);

/**
 * Owns the single WebSocket connection to the open case's
 * `/api/v1/cases/{cid}/events` (the URL comes from the case-scoped `api`).
 *
 * One connection for the whole app, mounted at the root: the server pushes the
 * full `Job` object in every envelope, so there is nothing a second connection
 * could learn that this one does not already have. Ordering, reconnection and
 * the `hello` snapshot are all handled inside `EventStream`; this component
 * only bridges its listeners into React state.
 *
 * Phase 3 (pipeline runner) consumes this same context — the runner UI is the
 * missing piece, not the plumbing.
 */
export function JobsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<JobState>(emptyJobState);
  const [status, setStatus] = useState<ConnectionStatus>('closed');
  const [cloudRevisions, setCloudRevisions] = useState<CloudRevisions>({});
  const [surveyChange, setSurveyChange] = useState<SurveyChangedEvent | null>(null);
  const [panoramasChange, setPanoramasChange] = useState<PanoramasChangedEvent | null>(null);
  const [posegraphChange, setPosegraphChange] = useState<PosegraphChangedEvent | null>(null);
  const [modelChange, setModelChange] = useState<ModelChangedEvent | null>(null);

  useEffect(() => {
    const stream = new EventStream({ url: api.eventsUrl() });
    const offState = stream.onState(setState);
    const offStatus = stream.onStatus(setStatus);
    const offClouds = stream.onCloudsChanged((names) => {
      // The 3D views' kept pages of these clouds are stale (`viewerCache.ts`).
      invalidateClouds(names);
      setCloudRevisions((revs) => bumpCloudRevisions(revs, names));
    });
    const offSurvey = stream.onSurveyChanged((event) => setSurveyChange({ ...event }));
    const offPanoramas = stream.onPanoramasChanged((event) => setPanoramasChange({ ...event }));
    const offPosegraph = stream.onPosegraphChanged((event) => setPosegraphChange({ ...event }));
    const offModel = stream.onModelChanged((event) => setModelChange({ ...event }));
    // The case was deleted under us: nothing here is valid any more.
    const offClosed = stream.onCaseClosed(() => window.location.assign(ALL_CASES_PATH));
    stream.start();
    return () => {
      offState();
      offStatus();
      offClouds();
      offSurvey();
      offPanoramas();
      offPosegraph();
      offModel();
      offClosed();
      stream.close();
    };
  }, []);

  // Seeded once, from the first `hello` (it names the project); a reconnect's
  // `hello` does not reseed, so a job that finished meanwhile is still news.
  const [history, setHistory] = useState<ReadonlySet<string> | null>(null);
  const helloSeen = state.project !== undefined;
  useEffect(() => {
    if (helloSeen && history === null) setHistory(finishedJobIds(jobsNewestFirst(state)));
  }, [helloSeen, history, state]);

  // The 3D views' cache (`viewerCache.ts`, frontend-38f) lives across route
  // switches, so it is kept fresh from here, the one place every event
  // reaches: a finished job may have rewritten a mesh under its old name,
  // and after a reconnect the events missed meanwhile are unknown.
  const finishedSeen = useRef(new Set<string>());
  useEffect(() => {
    if (noteFinishedJobs(finishedSeen.current, jobsNewestFirst(state))) invalidateMeshes();
  }, [state]);
  const everOpen = useRef(false);
  useEffect(() => {
    if (status !== 'open') return;
    if (everOpen.current) clearViewerCache();
    everOpen.current = true;
  }, [status]);

  const markCloudsChanged = useCallback(
    (names: readonly string[]) => {
      if (status === 'open') return; // the broadcast will arrive
      invalidateClouds(names);
      setCloudRevisions((revs) => bumpCloudRevisions(revs, names));
    },
    [status],
  );

  const value = useMemo<JobsContextValue>(
    () => ({
      state,
      status,
      jobs: jobsNewestFirst(state),
      active: activeJobs(state),
      cloudRevisions,
      markCloudsChanged,
      surveyChange,
      panoramasChange,
      posegraphChange,
      modelChange,
      history,
    }),
    [state, status, cloudRevisions, markCloudsChanged, surveyChange, panoramasChange, posegraphChange, modelChange, history],
  );

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>;
}

export function useJobs(): JobsContextValue {
  const value = useContext(JobsContext);
  if (!value) throw new Error('useJobs must be used inside <JobsProvider>');
  return value;
}
